import './LeftSidebar.styles.css';
import { type Key, useEffect, useState } from 'react';
import type { GetProps, TreeProps } from 'antd';
import { Input, Tree } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { fetchTopics } from '../../../topics/Topics.services.ts';
import { buildTree, buildTreeByAbbreviation } from '../../../../helpers';
import { TypeModal } from '../type-modal';
import { TreeModal } from '../../../../components/tree-model';
import { fetchOrganizationUnitLists } from '../../../organization-unit-lists/OrganizationUnitLists.services.ts';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { FiX } from 'react-icons/fi';
import { orderBy } from 'lodash';
import i18n from '../../../../utils/i18n.ts';

const { DirectoryTree } = Tree;

const plainOptions = [
  { value: 1, label: i18n.t('common.specification') },
  { value: 2, label: i18n.t('common.company-agreement') },
  { value: 3, label: i18n.t('common.implementation') },
];

export default function LeftSidebar() {
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [type, setType] = useState<number[]>([]);
  const [selectedKeys, setSelectedKeys] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
  const [expandedResponsibilities, setExpandedResponsibilities] = useState<Key[]>([]);
  const [responsibility, setResponsibility] = useState<number[] | string[]>([]);
  const [openTypeModal, setOpenTypeModal] = useState<boolean>(false);
  const [openResponsibilitiesModal, setOpenResponsibilitiesModal] = useState<boolean>(false);
  const search = searchParams.get('searchTerm');
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const hasAccount = accounts.length > 0;
  const { data: topics } = useQuery({
    queryKey: ['topics'],
    queryFn: fetchTopics,
    enabled: hasAccount,
    retry: false,
  });
  const { data: units } = useQuery({
    queryKey: ['organizationUnitLists'],
    queryFn: fetchOrganizationUnitLists,
    enabled: hasAccount,
    retry: false,
  });
  const showClear =
    type.length > 0 ||
    responsibility.length > 0 ||
    searchTerm !== '' ||
    searchParams.get('topicId') !== null ||
    searchParams.get('types') !== null ||
    searchParams.get('unitInChargeIds') !== null;

  const handleClearFilters = () => {
    searchParams.delete('topicId');
    searchParams.delete('types');
    searchParams.delete('unitInChargeIds');
    searchParams.delete('searchTerm');
    setSearchParams(searchParams);
    setType([]);
    setResponsibility([]);
    setSearchTerm('');
    setSelectedKeys('');
    setExpandedKeys([]);
    setExpandedResponsibilities([]);
  };

  type SearchProps = GetProps<typeof Input.Search>;

  const onSelect: TreeProps['onSelect'] = (selectedKeys) => {
    const value = selectedKeys[0];
    setSelectedKeys(selectedKeys[0].toString());
    if (value) {
      searchParams.set('topicId', value.toString());
      setSearchParams(searchParams);
    } else {
      searchParams.delete('topicId');
      setSearchParams(searchParams);
    }
    if (window.location.pathname.includes('regulation')) {
      navigate(`/?topicId=${value}`);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const onSearch: SearchProps['onSearch'] = (value) => {
    if (value) {
      searchParams.set('searchTerm', value);
      setSearchParams(searchParams);
    } else {
      searchParams.delete('searchTerm');
      setSearchParams(searchParams);
    }
    if (window.location.pathname.includes('regulation')) {
      navigate(`/?searchTerm=${value}`);
    }
  };

  const topicsOptions =
    topics?.length > 0
      ? buildTree(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          orderBy(topics, 'order', 'asc')?.map((topic: any, index: number) => ({
            id: topic.id,
            key: topic.id.toString() || index.toString(),
            parentId: topic.parentTopicId,
            title: topic.name,
            name: topic.name,
          })),
        )
      : [];

  const responsibilitiesOptions =
    units?.length > 0
      ? buildTreeByAbbreviation(
          orderBy(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            units.map((unit) => ({
              ...unit,
              key: unit.id,
              name: unit.title.replace(/^\d+\s*/, ''),
            })),
            'order',
            'asc',
          ),
        )
      : [];

  const handleOpenTypeModal = () => {
    setOpenResponsibilitiesModal(false);
    setOpenTypeModal(true);
  };

  const handleCloseTypeModal = () => {
    setOpenTypeModal(false);
  };

  const handleSubmitTypeModal = (types: number[]) => {
    searchParams.delete('types');
    if (types.length > 0) {
      types.forEach((type: number) => searchParams.append('types', type.toString()));
      setSearchParams(searchParams);
    } else {
      searchParams.delete('types');
      setSearchParams(searchParams);
    }
    setType(types);
    if (window.location.pathname.includes('regulation')) {
      navigate(`/?${types?.map((type) => `types=${type}`).join('&')}`);
    }
    setOpenTypeModal(false);
  };

  const handleSubmitResponsibilitiesModal = (responsibilities: string[] | number[]) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    const uniquResponibilities = [...new Set(responsibilities)];
    if (uniquResponibilities.length > 0) {
      searchParams.delete('unitInChargeIds');
      uniquResponibilities.forEach((rew: string | number) =>
        searchParams.append('unitInChargeIds', rew.toString()),
      );

      setSearchParams(searchParams);
    } else {
      searchParams.delete('unitInChargeIds');
      setSearchParams(searchParams);
    }
    if (window.location.pathname.includes('regulation')) {
      navigate(`/?${uniquResponibilities?.map((type) => `unitInChargeIds=${type}`).join('&')}`);
    }
    setResponsibility(() => uniquResponibilities);
    setOpenResponsibilitiesModal(false);
  };

  const handleOpenResponsibilitiesModal = () => {
    setOpenTypeModal(false);
    setOpenResponsibilitiesModal(true);
  };

  const handleCloseResponsibilitiesModal = () => {
    setOpenResponsibilitiesModal(false);
  };

  useEffect(() => {
    const searchTerm = searchParams.get('searchTerm');
    const topicId = searchParams.get('topicId');
    const types = searchParams.getAll('types');
    const unitInChargeIds = searchParams.getAll('unitInChargeIds');
    const topic = topics?.find((tpc: { id: string }) => tpc.id === topicId);
    setSelectedKeys(topicId || '');
    setResponsibility(unitInChargeIds || []);
    setType(types?.map(Number) || []);
    setSearchTerm(searchTerm || '');
    setExpandedKeys(topic?.parentTopicId ? [topic.parentTopicId.toString()] : []);
  }, []);

  return (
    <>
      <div className="section">
        {!search && (
          <Input.Search
            placeholder={t('form.placeholder.type-here')}
            variant="filled"
            size="large"
            value={searchTerm}
            onChange={handleSearch}
            onSearch={onSearch}
          />
        )}
      </div>
      <div className="section">
        <div className="label">{t('common.by-topic')}</div>
        <DirectoryTree
          onSelect={onSelect}
          showIcon={false}
          selectedKeys={[selectedKeys]}
          expandedKeys={expandedKeys}
          onExpand={(expandedKeys) => setExpandedKeys(expandedKeys)}
          treeData={topicsOptions}
          switcherIcon={<DownOutlined />}
        />
      </div>
      <div className="section">
        <div className="label">{t('common.by-type')}</div>
        <div
          className="section-link"
          style={
            type.length > 0
              ? { flexDirection: 'column', alignItems: 'flex-start' }
              : { flexDirection: 'row', alignItems: 'center' }
          }
        >
          {type.length > 0 ? (
            type?.map((item) => (
              <span>{plainOptions.find((opt) => opt.value === item)?.label}</span>
            ))
          ) : (
            <span>{t('common.not-selected')}</span>
          )}
          <div className="link" onClick={handleOpenTypeModal}>
            {t('common.select')} <RightOutlined style={{ fontSize: 14 }} />
          </div>
        </div>
      </div>
      <div className="section">
        <div className="label">{t('common.by-responsibilities')}</div>
        <div
          className="section-link"
          style={
            responsibility.length > 0
              ? { flexDirection: 'column', alignItems: 'flex-start' }
              : { flexDirection: 'row', alignItems: 'center' }
          }
        >
          {responsibility.length > 0 ? (
            responsibility?.map((item) => {
              return (
                <span>
                  {units?.find((unit: { id: number }) => unit.id === Number(item))?.title}
                </span>
              );
            })
          ) : (
            <span>{t('common.not-selected')}</span>
          )}
          <div className="link" onClick={handleOpenResponsibilitiesModal}>
            {t('common.select')} <RightOutlined style={{ fontSize: 14 }} />
          </div>
        </div>
      </div>
      {showClear && (
        <div className="section">
          <div className="link" onClick={handleClearFilters}>
            {t('common.clear-select')} <FiX style={{ fontSize: 20 }} />
          </div>
        </div>
      )}
      {openTypeModal && (
        <TypeModal
          open={openTypeModal}
          selected={type}
          onClose={handleCloseTypeModal}
          onSubmit={handleSubmitTypeModal}
        />
      )}
      {openResponsibilitiesModal && (
        <TreeModal
          title={t('common.by-responsibilities-2')}
          open={openResponsibilitiesModal}
          selected={responsibility.map((item) => item.toString())}
          expandedKeys={expandedResponsibilities}
          onExpand={setExpandedResponsibilities}
          treeData={responsibilitiesOptions}
          onClose={handleCloseResponsibilitiesModal}
          onSubmit={handleSubmitResponsibilitiesModal}
          isNumber
        />
      )}
    </>
  );
}
